import { useState } from 'react';
import { Upload, X } from 'lucide-react';
import toast from 'react-hot-toast';
import { api } from '../lib/api';

interface AvatarUploadProps {
  currentAvatarUrl?: string | null;
  onUploadSuccess: (avatarUrl: string) => void;
}

export function AvatarUpload({ currentAvatarUrl, onUploadSuccess }: AvatarUploadProps) {
  const [isUploading, setIsUploading] = useState(false);
  const [preview, setPreview] = useState<string | null>(null);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    // Validate file size (10MB max)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Image must be smaller than 10MB');
      return;
    }

    // Show preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setPreview(reader.result as string);
    };
    reader.readAsDataURL(file);

    // Upload file
    setIsUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await api.post('/uploads/avatar', formData);

      const avatarUrl = (response as any).data?.avatarUrl;
      if (avatarUrl) {
        onUploadSuccess(avatarUrl);
        toast.success('Avatar uploaded successfully!');
      } else {
        throw new Error('No avatar URL returned');
      }
    } catch (error) {
      console.error('Avatar upload failed:', error);
      toast.error('Failed to upload avatar');
      setPreview(null);
    } finally {
      setIsUploading(false);
    }
  };

  const displayUrl = preview || currentAvatarUrl;

  return (
    <div className="flex flex-col items-center gap-4">
      <div className="relative">
        {displayUrl ? (
          <img
            src={displayUrl}
            alt="Avatar"
            className="w-32 h-32 rounded-full object-cover border-4 border-gold/30"
          />
        ) : (
          <div className="w-32 h-32 rounded-full bg-leather border-4 border-gold/30 flex items-center justify-center">
            <Upload className="w-12 h-12 text-gold/50" />
          </div>
        )}

        {isUploading && (
          <div className="absolute inset-0 bg-black/50 rounded-full flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-2 border-gold border-t-transparent" />
          </div>
        )}

        {preview && !isUploading && (
          <button
            onClick={() => setPreview(null)}
            className="absolute top-0 right-0 bg-hot text-cream rounded-full p-1 hover:bg-hot-dark"
            title="Cancel"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      <label className="cursor-pointer">
        <input
          type="file"
          accept="image/*"
          onChange={handleFileChange}
          className="hidden"
          disabled={isUploading}
        />
        <span className="inline-flex items-center gap-2 px-4 py-2 bg-gold text-leather font-medium rounded-lg hover:bg-gold-bright transition-colors disabled:opacity-50">
          <Upload className="w-4 h-4" />
          {isUploading ? 'Uploading...' : 'Upload Avatar'}
        </span>
      </label>

      <p className="text-sm text-text-muted text-center">
        JPG, PNG, GIF, or WebP (max 10MB)
      </p>
    </div>
  );
}
